import clsx from 'clsx';
import { type ReactNode, forwardRef } from 'react';

import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import * as styles from './ChipContent.sty.js';
import { useChipContext } from './contexts/useChipContext.js';

//#region Typings
/**
 * Accepted properties for the Content
 * @public
 */
export interface ChipContentProps extends StylingProps, DataTestId {
  /** Elements to be displayed in the prefix or suffix slot. */
  children: ReactNode;
}
//#endregion

/**
 * ChipContent component, used for adding elements to the prefix/suffix slot.
 * @public
 */
export const ChipContent = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  ChipContentProps & { $isPrefixWithKey?: boolean }
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    $isPrefixWithKey,
    ...remainingProps
  } = props;
  const { size } = useChipContext();

  return (
    <div
      ref={forwardedRef}
      className={clsx(
        styles.content({
          size,
        }),
        consumerClassName,
      )}
      style={consumerStyle}
      data-testid={dataTestId}
      {...remainingProps}
    >
      {children}
    </div>
  );
});

/**
 * Component used for adding elements to the prefix slot.
 * @public
 * */
export const ChipPrefix = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  ChipContentProps
>((props, forwardedRef) => {
  const {
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...restProps
  }: ChipContentProps = props;
  const { hasKey } = useChipContext();

  return (
    <ChipContent
      data-testid={dataTestId}
      ref={forwardedRef}
      className={clsx(
        {
          [styles.prefix]: hasKey,
        },
        consumerClassName,
      )}
      style={consumerStyle}
      $isPrefixWithKey={hasKey}
      {...restProps}
    />
  );
});

/**
 * Component used for adding elements to the suffix slot.
 * @public
 */
export const ChipSuffix = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  ChipContentProps
>((props, forwardedRef) => {
  const {
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...restProps
  }: ChipContentProps = props;

  return (
    <ChipContent
      data-testid={dataTestId}
      ref={forwardedRef}
      className={consumerClassName}
      style={consumerStyle}
      {...restProps}
    />
  );
});

(ChipPrefix as typeof ChipPrefix & { displayName: string }).displayName =
  'Chip.Prefix';
(ChipSuffix as typeof ChipSuffix & { displayName: string }).displayName =
  'Chip.Suffix';
